
import { useState, Fragment } from "react";
import { ReportAPI } from "../../api/apiClient";
import { useUI } from "../../context/UIContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ======================================================
TYPES
====================================================== */

interface MaterialStatementRow {
  componentNo: number;
  componentGroup: string;
  component: string;
  unitName: string;
  date: string;
  opening: number;
  receipt: number;
  used: number;
  transfer: number;
  stock: number;
}

interface MaterialDateRow {
  date: string;
  opening: number;
  receipt: number;
  used: number;
  transfer: number;
  closing: number;
}

interface MaterialItem {
  component: string;
  unit: string;
  opening: number;
  receipt: number;
  used: number;
  transfer: number;
  closing: number;
  dates: MaterialDateRow[];
  expanded?: boolean;
}

interface MaterialGroup {
  componentGroup: string;
  items: MaterialItem[];
}

/* ======================================================
COMPONENT
====================================================== */

export default function MaterialStatementReport() {

  const { selectedProjectId, selectedProjectName } = useUI();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState<MaterialGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  /* ======================================================
  API CALL
  ====================================================== */

  const fetchReport = async () => {

    if (!selectedProjectId) {
      alert("No project selected");
      return;
    }

    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date");
      return;
    }

    setLoading(true);

    try {

      const data: MaterialStatementRow[] =
        await ReportAPI.getMaterialStatement({
          project: selectedProjectId,
          qDate: fromDate,
          endDate: toDate,
        });

      setRows(groupMaterials(data));

    } catch (err) {

      console.error("Failed to load material statement", err);
      setRows([]);

    } finally {

      setLoading(false);

    }
  };

  /* ======================================================
  GROUP MATERIALS (RUNNING CLOSING)
  ====================================================== */

  const groupMaterials = (data: MaterialStatementRow[]): MaterialGroup[] => {

    const groupMap: Record<string, MaterialGroup> = {};

    data.sort(
      (a, b) =>
        new Date(a.date).getTime() -
        new Date(b.date).getTime()
    );

    data.forEach((row) => {

      const groupName = row.componentGroup || "Others";

      if (!groupMap[groupName]) {
        groupMap[groupName] = {
          componentGroup: groupName,
          items: [],
        };
      }

      const items = groupMap[groupName].items;

      let material = items.find(
        (i) => i.component === row.component
      );

      if (!material) {

        material = {
          component: row.component,
          unit: row.unitName?.trim() || "",
          opening: row.opening ?? 0,
          receipt: 0,
          used: 0,
          transfer: 0,
          closing: 0,
          dates: [],
          expanded: false
        };

        items.push(material);
      }

      /* Running closing calculation */

      let previousClosing =
        material.dates.length === 0
          ? (row.opening ?? 0)
          : material.dates[material.dates.length - 1].closing;

      const closing =
        previousClosing +
        (row.receipt ?? 0) -
        (row.used ?? 0) -
        (row.transfer ?? 0);

      material.dates.push({
        date: row.date,
        opening: previousClosing,
        receipt: row.receipt ?? 0,
        used: row.used ?? 0,
        transfer: row.transfer ?? 0,
        closing: closing
      });

      material.receipt += row.receipt ?? 0;
      material.used += row.used ?? 0;
      material.transfer += row.transfer ?? 0;

      material.closing =
        material.opening +
        material.receipt -
        material.used -
        material.transfer;

    });

    return Object.values(groupMap);
  };

  /* ======================================================
  EXPAND / COLLAPSE
  ====================================================== */

  const toggleMaterial = (groupIndex: number, itemIndex: number) => {

    const updated = [...rows];

    updated[groupIndex].items[itemIndex].expanded =
      !updated[groupIndex].items[itemIndex].expanded;

    setRows(updated);
  };

  /* ======================================================
  DATE FORMAT FOR PDF
  ====================================================== */

  const formatReportDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  /* ======================================================
  EXPORT PDF
  ====================================================== */

  const exportPDF = () => {

    if (rows.length === 0) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF("landscape", "mm", "a4");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(selectedProjectName || "Project", 148, 15, { align: "center" });

    doc.setFontSize(13);
    doc.text("Material Statement Report", 148, 22, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      `From: ${formatReportDate(fromDate)}  To: ${formatReportDate(toDate)}`,
      148,
      28,
      { align: "center" }
    );
    doc.setFontSize(10);



    let startY = 35;

    rows.forEach((group) => {

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(group.componentGroup, 14, startY);

      startY += 4;

      const body: any[] = [];

      group.items.forEach((item) => {

        body.push([
          item.component,
          item.unit,
          item.opening.toFixed(2),
          item.receipt.toFixed(2),
          item.used.toFixed(2),
          item.transfer.toFixed(2),
          item.closing.toFixed(2)
        ]);

        if (item.expanded) {

          item.dates.forEach((d) => {

            body.push([
              "   " + new Date(d.date).toLocaleDateString(),
              item.unit,
              d.opening.toFixed(2),
              d.receipt.toFixed(2),
              d.used.toFixed(2),
              d.transfer.toFixed(2),
              d.closing.toFixed(2)
            ]);

          });

        }

      });

      autoTable(doc, {
        startY: startY,
        head: [[
          "Resource",
          "Unit",
          "Opening",
          "Receipt",
          "Used",
          "Transfer",
          "Closing"
        ]],
        body: body,
        theme: "grid",
        styles: { fontSize: 9, halign: "right" },
        headStyles: { fillColor: [40, 40, 40], textColor: 255, halign: "center" },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" }
        },
        margin: { left: 14, right: 14 },
      });

      startY = (doc as any).lastAutoTable.finalY + 6;

    });

    /* Page numbers */

    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {

      doc.setPage(i);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save("Material_Statement_Report.pdf");
  };

  /* ======================================================
  DATE FILTER SCREEN
  ====================================================== */

  if (!showReport) {

    return (

      <div className="p-6 space-y-4 max-w-xl">

        <h2 className="text-xl font-bold">
          Material Statement
        </h2>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            From Date
          </label>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border p-2 rounded w-full"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            To Date
          </label>

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border p-2 rounded w-full"
          />
        </div>

        <div className="flex gap-3 mt-4">

          <button
            onClick={() => {
              fetchReport();
              setShowReport(true);
            }}
            className="px-4 py-2 bg-green-600 text-white rounded"
          >
            Run Report
          </button>

          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-400 text-black rounded"
          >
            Back
          </button>

        </div>

      </div>

    );
  }

  /* ======================================================
  REPORT TABLE UI
  ====================================================== */

  return (

    <div className="p-6">

      <div className="flex justify-between items-center mb-6">

        <h2 className="text-xl font-bold">
          Material Statement
        </h2>

        <button
          onClick={exportPDF}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Export PDF
        </button>

      </div>

      {loading ? (

        <div className="text-gray-600">
          Loading...
        </div>

      ) : (

        <table className="w-full border text-sm">

          <thead className="bg-gray-800 text-white">

            <tr>
              <th className="border px-2 py-1 text-left">Resource</th>
              <th className="border px-2 py-1">Unit</th>
              <th className="border px-2 py-1">Opening</th>
              <th className="border px-2 py-1">Receipt</th>
              <th className="border px-2 py-1">Used</th>
              <th className="border px-2 py-1">Transfer</th>
              <th className="border px-2 py-1">Closing</th>
            </tr>

          </thead>

          <tbody>

            {rows.map((group, gi) => (

              <Fragment key={gi}>

                <tr className="bg-gray-300 font-semibold">
                  <td colSpan={7} className="border px-2 py-1">
                    {group.componentGroup}
                  </td>
                </tr>

                {group.items.map((item, i) => (

                  <Fragment key={i}>

                    <tr>

                      <td className="border px-2 py-1 pl-4">

                        <button
                          onClick={() => toggleMaterial(gi, i)}
                          className="mr-2 font-bold"
                        >
                          {item.expanded ? "-" : "+"}
                        </button>

                        {item.component}

                      </td>

                      <td className="border px-2 py-1 text-center">
                        {item.unit}
                      </td>

                      <td className="border px-2 py-1 text-right">
                        {item.opening.toFixed(2)}
                      </td>

                      <td className="border px-2 py-1 text-right">
                        {item.receipt.toFixed(2)}
                      </td>

                      <td className="border px-2 py-1 text-right">
                        {item.used.toFixed(2)}
                      </td>

                      <td className="border px-2 py-1 text-right">
                        {item.transfer.toFixed(2)}
                      </td>

                      <td className="border px-2 py-1 text-right">
                        {item.closing.toFixed(2)}
                      </td>

                    </tr>

                    {item.expanded &&
                      item.dates.map((d, di) => (

                        <tr key={di} className="bg-gray-100">

                          <td className="border px-2 py-1 pl-12">
                            {new Date(d.date).toLocaleDateString()}
                          </td>

                          <td className="border text-center">
                            {item.unit}
                          </td>

                          <td className="border text-right">
                            {d.opening.toFixed(2)}
                          </td>

                          <td className="border text-right">
                            {d.receipt.toFixed(2)}
                          </td>

                          <td className="border text-right">
                            {d.used.toFixed(2)}
                          </td>

                          <td className="border text-right">
                            {d.transfer.toFixed(2)}
                          </td>

                          <td className="border text-right">
                            {d.closing.toFixed(2)}
                          </td>

                        </tr>

                      ))}

                  </Fragment>

                ))}

              </Fragment>

            ))}

          </tbody>

        </table>

      )}

      <div className="mt-6">

        <button
          onClick={() => setShowReport(false)}
          className="px-4 py-2 bg-gray-400 text-black rounded"
        >
          Back
        </button>

      </div>

    </div>

  );

}

